import React, { memo, useState } from 'react';
import styled, { css } from 'styled-components';
import ModalContainer, { ButtonGroup } from '@/shared/components/modal/ModalContainer';
import StyledButton from '@/shared/components/atoms/StyledButton';
import { formatDate } from '@shared/utils/common';
import { CHECK_IN_METHOD_LABEL } from '../utils/utils';
import { checkInCancelReasons } from '../config';
import theme from '@/styles/theme';
import type { ReceptionParticipantItemDto } from '@/api/generated/model';

type ParticipantActionModalProps = {
	participant: ReceptionParticipantItemDto; // phoneNumber는 서버에서 maskPhone 파라미터에 따라 마스킹되어 내려옴
	onClose: () => void;
	onManualCheckIn: (participant: ReceptionParticipantItemDto) => void;
	onCancelCheckIn: (participant: ReceptionParticipantItemDto, reason: string) => void;
};

// 참가자 명단 클릭 시 노출되는 액션 모달
// - 미수령: 수동 체크인 / 수령: 체크인 취소
const ParticipantActionModal = memo<ParticipantActionModalProps>(({ participant, onClose, onManualCheckIn, onCancelCheckIn }) => {
	const isReceived = participant.status === 'checked';
	const [selectedReason, setSelectedReason] = useState('');

	const phoneNumber = participant.phoneNumber;

	return (
		<ModalContainer isFixed={true} maxWidth={480}>
			{/* 참가자 요약 */}
			<Head>
				<Name>{participant.name}</Name>
				<Course>{participant.course}</Course>
			</Head>
			<SubInfo>
				Bib {participant.bibNumber ?? '-'} · {phoneNumber}
			</SubInfo>

			{/* 상세 정보 */}
			<InfoList>
				<InfoRow>
					<InfoLabel>상태</InfoLabel>
					<InfoValue>{isReceived ? '체크인 완료(수령)' : '미수령'}</InfoValue>
				</InfoRow>
				{isReceived ? (
					<>
						<InfoRow>
							<InfoLabel>체크인 시점</InfoLabel>
							<InfoValue>{formatDate(participant.checkinTime ?? undefined, 'YY.MM.DD HH:mm')}</InfoValue>
						</InfoRow>
						<InfoRow>
							<InfoLabel>처리 담당자</InfoLabel>
							<InfoValue>{participant.manager || '-'}</InfoValue>
						</InfoRow>
						<InfoRow>
							<InfoLabel>처리 방식</InfoLabel>
							<InfoValue>{participant.type ? CHECK_IN_METHOD_LABEL[participant.type] : '-'}</InfoValue>
						</InfoRow>
					</>
				) : (
					<InfoRow>
						<InfoLabel>생년월일</InfoLabel>
						<InfoValue>{formatDate(participant.birthDate ?? undefined, 'YYYY.MM.DD')}</InfoValue>
					</InfoRow>
				)}
			</InfoList>

			<Divider />

			{/* 액션 영역 */}
			{isReceived ? (
				<Section>
					<SectionTitle $tone="orange">
						체크인 취소<Asterisk>*</Asterisk>
					</SectionTitle>
					<SectionDescription>취소하면 미수령 상태로 되돌아갑니다. 사유를 선택하세요.</SectionDescription>
					<ChipList>
						{checkInCancelReasons.map(reason => (
							<Chip key={reason} $active={selectedReason === reason} onClick={() => setSelectedReason(reason)}>
								{reason}
							</Chip>
						))}
					</ChipList>
				</Section>
			) : (
				<Section>
					<SectionTitle $tone="blue">
						수동 체크인<Asterisk $tone="blue">*</Asterisk>
					</SectionTitle>
					<SectionDescription>QR 없이 수동으로 체크인 처리합니다. 참가자 신원 확인 후 진행하세요.</SectionDescription>
				</Section>
			)}

			<ButtonGroup>
				<StyledButton width={160} onClick={onClose}>
					닫기
				</StyledButton>
				{isReceived ? (
					<StyledButton
						width={220}
						fontColor={theme.colors.whiteColor}
						bgColor={theme.colors.activeRedOrange}
						border="none"
						disabled={!selectedReason}
						onClick={() => onCancelCheckIn(participant, selectedReason)}>
						체크인 취소
					</StyledButton>
				) : (
					<StyledButton
						width={220}
						fontColor={theme.colors.whiteColor}
						bgColor={theme.colors.activeBlue}
						border="none"
						onClick={() => onManualCheckIn(participant)}>
						수동 체크인
					</StyledButton>
				)}
			</ButtonGroup>
		</ModalContainer>
	);
});

ParticipantActionModal.displayName = 'ParticipantActionModal';

export default ParticipantActionModal;

const Head = styled.div`
	display: flex;
	align-items: baseline;
	gap: 10px;
`;
const Name = styled.div`
	font-size: 18px;
	font-family: PretendardSemiBold, AppleSDGothicNeoSemiBold, sans-serif;
`;
const Course = styled.div`
	font-size: 14px;
	color: ${theme.colors.deepGrayFontColor};
`;
const SubInfo = styled.div`
	font-size: 14px;
	color: ${theme.colors.lightGrayFontColor};
	margin-top: 10px;
`;
const InfoList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	margin-top: 28px;
`;
const InfoRow = styled.div`
	display: flex;
	align-items: center;
`;
const InfoLabel = styled.div`
	width: 120px;
	font-size: 14px;
	color: ${theme.colors.deepGrayFontColor};
`;
const InfoValue = styled.div`
	font-size: 14px;
	font-family: PretendardMedium, AppleSDGothicNeoMedium, sans-serif;
	color: ${theme.colors.blackColor};
`;
const Divider = styled.div`
	height: 1px;
	margin: 28px 0;
	background-color: ${theme.colors.ultraLightGrayBorder};
`;
const Section = styled.div``;
const SectionTitle = styled.div<{ $tone: 'orange' | 'blue' }>`
	font-size: 15px;
	font-family: PretendardSemiBold, AppleSDGothicNeoSemiBold, sans-serif;
	color: ${({ $tone }) => ($tone === 'orange' ? theme.colors.activeRedOrange : theme.colors.activeBlue)};
`;
const Asterisk = styled.span<{ $tone?: 'blue' }>`
	color: ${({ $tone }) => ($tone === 'blue' ? theme.colors.activeBlue : theme.colors.activeRedOrange)};
	margin-left: 2px;
`;
const SectionDescription = styled.div`
	font-size: 14px;
	color: ${theme.colors.deepGrayFontColor};
	margin-top: 12px;
`;
const ChipList = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	margin-top: 20px;
`;
const Chip = styled.button<{ $active: boolean }>`
	font-size: 14px;
	padding: 10px 16px;
	border-radius: 6px;
	cursor: pointer;
	color: ${theme.colors.deepGrayFontColor};
	border: 1px solid ${theme.colors.lightGrayBorder};
	background-color: ${theme.colors.whiteColor};
	transition: 0.2s;

	&:hover {
		background-color: ${theme.colors.lightGrayHoverColor};
	}

	${({ $active }) =>
		$active &&
		css`
			font-family: PretendardMedium, AppleSDGothicNeoMedium, sans-serif;
			color: ${theme.colors.activeRedOrange};
			border-color: ${theme.colors.activeRedOrange};
			background-color: #fff1e8;
		`};
`;
